<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration, withAlpha } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 500));

const accent = computed(() => props.block.accentColor || "#7dd3fc");
const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
      fontFamily: props.block.fontFamily || "'Inter', system-ui, sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-accent": accent.value,
      "--cd-glow": withAlpha(accent.value, 0.45),
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.4}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 24}px`,
  };
});
</script>

<template>
  <div class="cd-glass" :style="containerStyle">
    <div class="cd-glass__orb cd-glass__orb--a" />
    <div class="cd-glass__orb cd-glass__orb--b" />
    <div class="cd-glass__orb cd-glass__orb--c" />

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-glass__card"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 110}ms`,
      }"
    >
      <div class="cd-glass__shine" />
      <div
        class="cd-glass__digit"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#ffffff',
        }"
      >
        <span :key="seg.value">{{ seg.value }}</span>
      </div>
      <div
        class="cd-glass__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || 'rgba(255,255,255,0.75)',
        }"
      >
        {{ seg.label }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-glass {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  overflow: hidden;
  isolation: isolate;
}
.cd-glass__orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.65;
  z-index: 0;
  pointer-events: none;
}
.cd-glass__orb--a {
  width: 260px;
  height: 260px;
  background: var(--cd-accent);
  top: -80px;
  left: -60px;
  animation: cd-glass-float 14s ease-in-out infinite;
}
.cd-glass__orb--b {
  width: 220px;
  height: 220px;
  background: #f472b6;
  bottom: -60px;
  right: -40px;
  animation: cd-glass-float 18s ease-in-out infinite reverse;
}
.cd-glass__orb--c {
  width: 180px;
  height: 180px;
  background: #34d399;
  top: 30%;
  left: 40%;
  animation: cd-glass-float 22s ease-in-out infinite;
}
@keyframes cd-glass-float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -20px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 30px) scale(0.95);
  }
}
.cd-glass__card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    0 0 30px var(--cd-glow);
  overflow: hidden;
  animation: cd-glass-rise 800ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
  transition:
    transform 0.4s ease,
    box-shadow 0.4s ease;
}
.cd-glass__card:hover {
  transform: translateY(-4px);
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 0 50px var(--cd-glow);
}
@keyframes cd-glass-rise {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.cd-glass__shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 35%,
    rgba(255, 255, 255, 0.2) 50%,
    transparent 65%
  );
  background-size: 200% 100%;
  animation: cd-glass-sheen 6s ease-in-out infinite;
  pointer-events: none;
}
@keyframes cd-glass-sheen {
  0%,
  100% {
    background-position: 200% 0;
  }
  50% {
    background-position: -50% 0;
  }
}
.cd-glass__digit {
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}
.cd-glass__digit span {
  display: inline-block;
  animation: cd-glass-tick var(--cd-tick, 500ms) ease-out;
}
@keyframes cd-glass-tick {
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
.cd-glass__label {
  margin-top: 6px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}
</style>
